// NeoCore · Cumplimiento › Casos AML (ADR 023)
// -----------------------------------------------------------------------------
// Bandeja de casos AML. Primer origen: verificaciones OkID que no dieron `pass`
// (actualización de datos o inscripción). Detalle: RENAP vs selfie, motivos, datos
// del asociado y bitácora; acciones tomar / comentar / cerrar (RPC caso_aml_accion).
// Mientras el caso está abierto el asociado opera normal.
// Backend: migración 0380 (⚠️ no aplicada hasta OK de Hugo).
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowsClockwise, ShieldWarning, X } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

type Caso = {
  id: string; numero: number; asociado_id: string; origen: string; verificacion_id: string | null
  motivo: string; prioridad: 'baja' | 'media' | 'alta'; estado: string; asignado_a: string | null
  resolucion: string | null; created_at: string; cerrado_at: string | null
  asociado?: { nombre_completo?: string; neo_id?: string; dpi?: string } | null
  verif?: { decision?: string; match_pct?: number | null; renap_encontrado?: boolean | null; facial_match?: boolean | null
    datos_coinciden?: boolean | null; vigencia_documento?: string | null; fotos?: Record<string, string> | null; pdf_path?: string | null } | null
}
type Evento = { id: string; tipo: string; texto: string | null; created_at: string; actor_user_id: string | null }

const ESTADOS: Record<string, { label: string; fg: string; bg: string }> = {
  abierto: { label: 'Abierto', fg: '#b91c1c', bg: '#fef2f2' },
  en_analisis: { label: 'En análisis', fg: '#b45309', bg: '#fffbeb' },
  cerrado_sin_hallazgo: { label: 'Cerrado · sin hallazgo', fg: '#047857', bg: '#ecfdf5' },
  cerrado_con_hallazgo: { label: 'Cerrado · con hallazgo', fg: '#7c2d12', bg: '#fff7ed' },
  reportado_ive: { label: 'Reportado a IVE', fg: '#1e3a8a', bg: '#eff6ff' },
}
const ORIGEN: Record<string, string> = {
  okid_actualizacion: 'OkID · actualización de datos', okid_inscripcion: 'OkID · inscripción', okid_neocore: 'OkID · NeoCore', manual: 'Manual',
}
const PRIO: Record<string, string> = { alta: '#b91c1c', media: '#b45309', baja: '#52525b' }
const fecha = (iso: string) => new Date(iso).toLocaleString('es-GT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'America/Guatemala' })

function Chip({ estado }: { estado: string }) {
  const e = ESTADOS[estado] ?? { label: estado, fg: '#52525b', bg: '#f4f4f5' }
  return <span style={{ background: e.bg, color: e.fg, borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{e.label}</span>
}

export function NeoCoreCasosAmlPage() {
  const [rows, setRows] = useState<Caso[]>([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState<'abiertos' | 'cerrados' | 'todos'>('abiertos')
  const [sel, setSel] = useState<Caso | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('caso_aml')
      .select('*, asociado:asociado_id(nombre_completo, neo_id, dpi), verif:verificacion_id(decision, match_pct, renap_encontrado, facial_match, datos_coinciden, vigencia_documento, fotos, pdf_path)')
      .order('created_at', { ascending: false }).limit(300)
    setRows((data as Caso[]) ?? [])
    setLoading(false)
  }, [])
  useEffect(() => { void load() }, [load])

  const abiertos = (c: Caso) => c.estado === 'abierto' || c.estado === 'en_analisis'
  const visibles = useMemo(() => rows.filter((c) => filtro === 'todos' || (filtro === 'abiertos' ? abiertos(c) : !abiertos(c))), [rows, filtro])
  const n = useMemo(() => ({ abiertos: rows.filter(abiertos).length, cerrados: rows.filter((c) => !abiertos(c)).length, todos: rows.length }), [rows])

  const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: 11, color: 'var(--muted-foreground)', fontWeight: 600, borderBottom: '1px solid var(--border)' }
  const td: React.CSSProperties = { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid var(--border)', verticalAlign: 'top' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <ShieldWarning size={26} weight="duotone" />
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Casos AML</h1>
          <p style={{ color: 'var(--muted-foreground)', fontSize: 13, margin: '2px 0 0' }}>Verificaciones de identidad que OkID no confirmó y casos de cumplimiento. El asociado opera normal mientras el caso está abierto.</p>
        </div>
        <button onClick={() => void load()} style={{ border: '1px solid var(--border)', background: 'transparent', borderRadius: 8, padding: '7px 10px', cursor: 'pointer' }}><ArrowsClockwise size={16} /></button>
      </div>

      <div style={{ display: 'flex', gap: 6 }}>
        {(['abiertos', 'cerrados', 'todos'] as const).map((k) => (
          <button key={k} onClick={() => setFiltro(k)} style={{ border: '1px solid var(--border)', borderRadius: 999, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: filtro === k ? 'var(--foreground, #111)' : 'transparent', color: filtro === k ? 'var(--background, #fff)' : undefined }}>
            {k[0].toUpperCase() + k.slice(1)} · {n[k]}
          </button>
        ))}
      </div>

      {loading ? <p style={{ fontSize: 13 }}>Cargando…</p> : visibles.length === 0 ? (
        <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--muted-foreground)' }}>Sin casos {filtro === 'todos' ? '' : filtro}.</div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>#</th><th style={th}>Fecha</th><th style={th}>Asociado</th><th style={th}>Origen</th><th style={th}>Motivo</th><th style={th}>Prioridad</th><th style={th}>Estado</th></tr></thead>
            <tbody>{visibles.map((c) => (
              <tr key={c.id} onClick={() => setSel(c)} style={{ cursor: 'pointer' }}>
                <td style={td}>{c.numero}</td><td style={td}>{fecha(c.created_at)}</td>
                <td style={td}><b>{c.asociado?.nombre_completo ?? '—'}</b><div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{c.asociado?.neo_id}</div></td>
                <td style={td}>{ORIGEN[c.origen] ?? c.origen}</td><td style={{ ...td, maxWidth: 360 }}>{c.motivo}</td>
                <td style={{ ...td, color: PRIO[c.prioridad], fontWeight: 700, textTransform: 'capitalize' }}>{c.prioridad}</td><td style={td}><Chip estado={c.estado} /></td>
              </tr>))}</tbody>
          </table>
        </div>
      )}

      {sel && <DetalleCaso caso={sel} onClose={() => setSel(null)} onChange={() => { void load(); setSel(null) }} />}
    </div>
  )
}

function DetalleCaso({ caso, onClose, onChange }: { caso: Caso; onClose: () => void; onChange: () => void }) {
  const [fotos, setFotos] = useState<Record<string, string>>({})
  const [eventos, setEventos] = useState<Evento[]>([])
  const [texto, setTexto] = useState('')
  const [cierre, setCierre] = useState('cerrado_sin_hallazgo')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const abierto = caso.estado === 'abierto' || caso.estado === 'en_analisis'

  useEffect(() => {
    supabase.from('caso_aml_evento').select('*').eq('caso_id', caso.id).order('created_at').then(({ data }) => setEventos((data as Evento[]) ?? []))
    const f = caso.verif?.fotos ?? {}
    const entries = Object.entries(f).filter(([k]) => k === 'renap' || k === 'selfie' || k === 'dpi_front')
    if (entries.length) {
      supabase.storage.from('okid').createSignedUrls(entries.map(([, p]) => p), 600).then(({ data }) => {
        const out: Record<string, string> = {}
        entries.forEach(([k], i) => { const u = data?.[i]?.signedUrl; if (u) out[k] = u })
        setFotos(out)
      })
    }
  }, [caso])

  const accion = async (a: 'tomar' | 'comentar' | 'cerrar') => {
    setBusy(true); setErr(null)
    const { error } = await supabase.rpc('caso_aml_accion', { p_caso: caso.id, p_accion: a, p_texto: texto || null, p_estado: a === 'cerrar' ? cierre : null })
    setBusy(false)
    if (error) { setErr(error.message); return }
    if (a === 'comentar') {
      setTexto('')
      const { data } = await supabase.from('caso_aml_evento').select('*').eq('caso_id', caso.id).order('created_at')
      setEventos((data as Evento[]) ?? [])
    } else onChange()
  }

  const v = caso.verif
  const fila = (k: string, val: React.ReactNode) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}><span style={{ color: 'var(--muted-foreground)' }}>{k}</span><span style={{ fontWeight: 600, textAlign: 'right' }}>{val}</span></div>
  )
  const si = (b?: boolean | null) => (b == null ? '—' : b ? '✓ sí' : '✗ no')
  const foto = (k: string, l: string) => (
    <figure style={{ margin: 0, flex: 1 }}>
      <div style={{ aspectRatio: '3/4', borderRadius: 12, overflow: 'hidden', background: 'var(--muted, #f4f4f5)', display: 'grid', placeItems: 'center' }}>
        {fotos[k] ? <img src={fotos[k]} alt={l} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>sin foto</span>}
      </div>
      <figcaption style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, marginTop: 6, textAlign: 'center' }}>{l}</figcaption>
    </figure>
  )

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 50, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(560px, 100%)', height: '100%', overflowY: 'auto', background: 'var(--background, #fff)', padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>Caso #{caso.numero} · {ORIGEN[caso.origen] ?? caso.origen}</div>
            <h2 style={{ margin: '4px 0', fontSize: 19 }}>{caso.asociado?.nombre_completo} <span style={{ fontWeight: 400, color: 'var(--muted-foreground)' }}>· {caso.asociado?.neo_id}</span></h2>
            <Chip estado={caso.estado} />
          </div>
          <button onClick={onClose} style={{ border: 0, background: 'transparent', cursor: 'pointer' }}><X size={20} /></button>
        </div>

        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: 12, fontSize: 13 }}><b>Motivo:</b> {caso.motivo}</div>

        {v && (
          <>
            <div style={{ display: 'flex', gap: 12 }}>{foto('renap', 'RENAP')}{foto('selfie', 'SELFIE')}{foto('dpi_front', 'DPI')}</div>
            <div>
              {fila('Decisión OkID', v.decision ?? '—')}
              {fila('Coincidencia facial', v.match_pct != null ? `${Math.round(v.match_pct)}%` : '—')}
              {fila('RENAP encontrado', si(v.renap_encontrado))}
              {fila('Rostro coincide', si(v.facial_match))}
              {fila('Datos DPI = RENAP', si(v.datos_coinciden))}
              {fila('Vigencia DPI', v.vigencia_documento ?? '—')}
            </div>
          </>
        )}

        <div>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Bitácora</div>
          {eventos.map((e) => (
            <div key={e.id} style={{ fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ color: 'var(--muted-foreground)', fontSize: 11 }}>{fecha(e.created_at)} · {e.tipo}</span>
              <div>{e.texto}</div>
            </div>
          ))}
        </div>

        {caso.resolucion && <div style={{ fontSize: 13 }}><b>Resolución:</b> {caso.resolucion}</div>}

        {abierto && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={3} placeholder="Comentario o resolución…" style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 10, font: 'inherit', fontSize: 13 }} />
            {err && <div style={{ color: '#b91c1c', fontSize: 12 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {!caso.asignado_a && <button disabled={busy} onClick={() => void accion('tomar')} style={btn(false)}>Tomar caso</button>}
              <button disabled={busy || texto.trim().length < 3} onClick={() => void accion('comentar')} style={btn(false)}>Comentar</button>
              <select value={cierre} onChange={(e) => setCierre(e.target.value)} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '6px 8px', fontSize: 13 }}>
                <option value="cerrado_sin_hallazgo">Sin hallazgo</option>
                <option value="cerrado_con_hallazgo">Con hallazgo</option>
                <option value="reportado_ive">Reportado a IVE</option>
              </select>
              <button disabled={busy || texto.trim().length < 10} onClick={() => void accion('cerrar')} style={btn(true)}>Cerrar caso</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const btn = (primario: boolean): React.CSSProperties => ({
  border: primario ? 0 : '1px solid var(--border)', background: primario ? 'var(--brand, #ff5b04)' : 'transparent',
  color: primario ? '#fff' : undefined, borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
})
